import axios from 'axios';

// Build a consistent base URL that ALWAYS includes "/api"
// Accepts either REACT_APP_API_URL with or without trailing "/api"
const RAW_API = process.env.REACT_APP_API_URL || 'http://localhost:3000'; // e.g., https://api.example.com or https://api.example.com/api
// Normalize: remove trailing slashes and trailing '/api', then append exactly one '/api'
const BASE = RAW_API.replace(/\/+$/, '').replace(/\/api$/, '');
const API_URL = `${BASE}/api`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add a request interceptor to include the token in headers
api.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('jwt');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch { }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle expired tokens
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      const requestUrl = (error.config && error.config.url) || '';
      // No forzar redirect en endpoints de autenticación para poder mostrar mensajes de error (ej: credenciales inválidas)
      const isAuthEndpoint = /\/auth\/(login|register|google|refresh)\b/.test(requestUrl);
      if (!isAuthEndpoint) {
        console.warn('Token expirado o inválido. Redirigiendo al login...');
        // Clear stored auth data
        localStorage.removeItem('token');
        localStorage.removeItem('jwt');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        // Redirect to login page
        window.location.href = '/login';
        return Promise.reject(new Error('Token expirado. Por favor inicia sesión nuevamente.'));
      }
    }
    return Promise.reject(error);
  }
);

export const register = (userData) => {
  return api.post('/auth/register', userData);
};

// ====== PASSWORD RESET ======
export const forgotPassword = async (emailOrPayload) => {
  try {
    const payload = typeof emailOrPayload === 'string' ? { email: emailOrPayload } : (emailOrPayload || {});
    await api.post('/auth/forgot-password', payload);
    return { success: true };
  } catch (error) {
    // Respuesta neutra en backend; mostramos ok de todas formas
    return { success: true };
  }
};

export const validateResetToken = async (token) => {
  try {
    const { data } = await api.get('/auth/reset-password', { params: { token } });
    return !!data?.valid;
  } catch {
    return false;
  }
};

export const resetPassword = async (token, password) => {
  try {
    const { data } = await api.post('/auth/reset-password', { token, password });
    return { success: !!data?.ok };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || 'No se pudo restablecer la contraseña' };
  }
};

// ====== AUTH PROFILE ======
export const getMe = () => api.get('/auth/me');
export const completeProfile = (payload) => api.post('/auth/complete-profile', payload);
export const updateMe = (payload) => api.put('/auth/me', payload);

// ====== EMAIL CHANGE ======
export const requestEmailChange = async ({ newEmail, website, human } = {}) => {
  try {
    await api.post('/auth/request-email-change', { newEmail, website, human });
    return { success: true };
  } catch (error) {
    return { success: true }; // respuesta neutra
  }
};

export const confirmEmailChange = async (token) => {
  try {
    const { data } = await api.post('/auth/confirm-email-change', { token });
    return { success: !!data?.ok, message: data?.message };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || 'No se pudo confirmar el cambio' };
  }
};

export const login = async (credentials) => {
  try {
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return { success: true, ...response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || 'Error en el servidor' };
  }
};

// Google login: exchange Google ID token for our backend JWT
export const loginWithGoogle = async (idToken) => {
  try {
    const response = await api.post('/auth/google', { id_token: idToken });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return { success: true, ...response.data };
  } catch (error) {
    return { success: false, error: error.response?.data?.message || 'Error en el servidor' };
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  // Optionally, you can also remove user info from local storage
  // localStorage.removeItem('user');
};

export const getServices = () => {
  return api.get('/services');
};

export const getColaboradores = (params = {}) => {
  return api.get('/colaboradores', { params });
};

// (removed) boxes & availability API helpers

// Funciones adicionales para GestiÃ³n de colaboradores
export const getColaborador = (colaboradorId) => {
  return api.get(`/colaboradores/${colaboradorId}`);
};

export const createColaborador = (colaboradorData) => {
  return api.post('/colaboradores', colaboradorData);
};

export const updateColaborador = (colaboradorId, colaboradorData) => {
  return api.put(`/colaboradores/${colaboradorId}`, colaboradorData);
};

export const deleteColaborador = (colaboradorId) => {
  return api.delete(`/colaboradores/${colaboradorId}`);
};

export const createAppointment = (bookingDetails) => {
  return api.post('/appointments', bookingDetails);
};

export const precheckAppointment = (params) => {
  // Accepts collaborator_id, service_id, start_time, duration, and optional box_id/auto_assign
  return api.get('/appointments', { params });
};

export const validateCoupon = (code, serviceId, collaboratorId) => {
  return api.post('/coupons/validate', { code, service_id: serviceId, collaborator_id: collaboratorId });
};

// Obtener citas del usuario autenticado
export const getMyAppointments = (params = {}) => {
  // Backend returns appointments for the current token's user
  // Optional params: { as: 'patient'|'collaborator', onlyUpcoming: 1|0, excludeStatuses: 'cancelada,completada' }
  return api.get('/my-appointments', { params });
};

// ====== CITAS (APPOINTMENTS) CRUD ======
// Nota: El backend ya expone /api/citas (GET, POST) y /api/citas/[id] (GET, PUT, DELETE)
export const getCitas = (params = {}) => api.get('/citas', { params });
export const createCita = (data) => api.post('/citas', data);
export const updateCita = (id, data) => api.put(`/citas/${id}`, data);
export const deleteCita = (id) => api.delete(`/citas/${id}`);
export const getCitaById = (id) => api.get(`/citas/${id}`);

// Obtener pacientes del colaborador autenticado
export const getMyPatients = async () => {
  // Ajusta el endpoint si tu backend usa otra ruta
  const { data } = await api.get('/my-patients');
  return data;
};

// Obtener todos los pacientes (no solo "mis" pacientes)
export const getTodosPacientes = async () => {
  const tryGet = async (path) => {
    try {
      const { data } = await api.get(path);
      return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    } catch (_) {
      return [];
    }
  };

  // Traer de múltiples fuentes y unir
  const sources = await Promise.all([
    tryGet('/pacientes'),
    tryGet('/clientes'),
    tryGet('/users'),
    tryGet('/my-patients'),
  ]);

  const merged = [].concat(...sources);

  // Normalizar a { id, nombre, apellido, email }
  const normalized = merged.map((p) => ({
    id: p.id ?? p.usuario_id ?? p.cliente_id ?? p.paciente_id,
    nombre: p.nombre ?? p.first_name ?? p.given_name ?? '',
    apellido: p.apellido ?? p.last_name ?? p.family_name ?? '',
    email: p.email ?? p.correo ?? p.mail ?? '',
  })).filter((p) => p.id);

  // Deduplicar por id o combinación id+email
  const seen = new Set();
  const out = [];
  for (const p of normalized) {
    const key = `${String(p.id)}::${(p.email || '').toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(p);
    }
  }
  return out;
};

// ====== BLOCKED SLOTS ======
export const getBlockedSlots = () => api.get('/blocked-slots');
export const createBlockedSlot = (payload) => api.post('/blocked-slots', payload);
export const deleteBlockedSlot = (id) => api.delete(`/blocked-slots/${id}`);

// ====== CLINICAL NOTES ======
export const getCitaNotas = async (citaId) => {
  const { data } = await api.get(`/citas/${citaId}/notas`);
  return data;
};

export const createCitaNota = async (citaId, payload) => {
  // payload: { tipo: 'SOAP'|'LIBRE', contenido: {...} | string }
  const { data } = await api.post(`/citas/${citaId}/notas`, payload);
  return data;
};

export const getClinicalRecord = async (citaId) => {
  const { data } = await api.get(`/citas/${citaId}/registro-clinico`);
  return data;
};

export const saveClinicalRecord = async (citaId, payload) => {
  const { data } = await api.post(`/citas/${citaId}/registro-clinico`, payload);
  return data;
};

// ====== FORMS (CRUD) ======
export const listForms = async () => {
  const { data } = await api.get('/forms');
  return data;
};

export const createForm = async (payload) => {
  // payload: { titulo: string, schema: object|string, is_public?: boolean }
  const { data } = await api.post('/forms', payload);
  return data;
};

export const getForm = async (formId) => {
  const { data } = await api.get(`/forms/${formId}`);
  return data;
};

export const updateForm = async (formId, payload) => {
  const { data } = await api.put(`/forms/${formId}`, payload);
  return data;
};

export const deleteForm = async (formId) => {
  const { data } = await api.delete(`/forms/${formId}`);
  return data;
};

// ====== SESSION FORM RESPONSES ======
export const getCitaFormResponses = async (citaId) => {
  const { data } = await api.get(`/citas/${citaId}/formularios`);
  return data;
};

export const submitCitaFormResponse = async (citaId, payload) => {
  // payload: { form_id: number, respuestas: object|string, paciente_id?: number }
  const { data } = await api.post(`/citas/${citaId}/formularios`, payload);
  return data;
};

// ====== PACIENTES (DETALLE) ======
export const getPacienteById = async (pacienteId) => {
  const { data } = await api.get(`/pacientes/${pacienteId}`);
  return data;
};

export const getPacienteHistorial = async (pacienteId) => {
  // LÃƒÂ­nea base: historial agregado (citas, notas, documentos). Ajusta al backend real si difiere
  const { data } = await api.get(`/pacientes/${pacienteId}/historial`);
  return data;
};

// ====== PACIENTE FICHAS (CRUD) ======
export const listPacienteFichas = async (pacienteId) => {
  const { data } = await api.get(`/pacientes/${pacienteId}/fichas`);
  return data;
};

export const createPacienteFicha = async (pacienteId, payload) => {
  // payload: { form_id: number, respuestas: object|string, cita_id?: number }
  const { data } = await api.post(`/pacientes/${pacienteId}/fichas`, payload);
  return data;
};

export const getPacienteFicha = async (pacienteId, fichaId) => {
  const { data } = await api.get(`/pacientes/${pacienteId}/fichas/${fichaId}`);
  return data;
};

export const updatePacienteFicha = async (pacienteId, fichaId, payload) => {
  // payload: { respuestas: object|string }
  const { data } = await api.put(`/pacientes/${pacienteId}/fichas/${fichaId}`, payload);
  return data;
};

export const deletePacienteFicha = async (pacienteId, fichaId) => {
  const { data } = await api.delete(`/pacientes/${pacienteId}/fichas/${fichaId}`);
  return data;
};

export const getAvailability = async (collaboratorId, date, options = {}) => {
  try {
    const { serviceId, duration } = options;
    const response = await api.get('/availability', {
      params: {
        collaborator_id: collaboratorId,
        date: date,
        // Send both to maximize compatibility across API versions
        ...(serviceId ? { service_id: serviceId } : {}),
        ...(duration ? { duration } : {}),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching availability:', error);
    throw error;
  }
};

export const getProjects = () => api.get('/projects');

// FunciÃƒÂ³n para crear un nuevo miembro del equipo (colaborador)
export const createMember = (memberData) => api.post('/Colaboradores', memberData);

// FunciÃƒÂ³n para crear un nuevo servicio
export const createService = (serviceData) => api.post('/services', serviceData);

// FunciÃƒÂ³n para obtener todos los usuarios/clientes
export const getUsers = () => api.get('/users');

// FunciÃƒÂ³n para obtener un miembro especÃƒÂ­fico por ID
export const getMember = (memberId) => api.get(`/team/${memberId}`);

// FunciÃƒÂ³n para actualizar un miembro
export const updateMember = (memberId, memberData) => api.put(`/team/${memberId}`, memberData);

// FunciÃƒÂ³n para obtener un servicio especÃƒÂ­fico por ID
export const getService = (serviceId) => api.get(`/services/${serviceId}`);

// FunciÃƒÂ³n para actualizar un servicio
export const updateService = (serviceId, serviceData) => api.put(`/services/${serviceId}`, serviceData);

// ========== TURNOS API FUNCTIONS ==========

// FunciÃƒÂ³n para obtener todos los turnos
export const getTurnos = () => api.get('/turnos');

// FunciÃ³n para crear un nuevo turno
export const createTurno = (turnoData) => api.post('/turnos', turnoData);

// ====== MARKETING (ADMIN) ======
export const getMarketingOverview = () => api.get('/admin/marketing/overview');
export const saveMarketingCampaign = (payload) => api.post('/admin/marketing/campaigns', payload);
export const saveMarketingSpecialPrice = (payload) => api.post('/admin/marketing/prices', payload);
export const saveMarketingPackage = (payload) => api.post('/admin/marketing/packages', payload);
export const deleteMarketingPackage = (packageId) => api.delete(`/admin/marketing/packages?id=${packageId}`);
export const applyCampaignServices = (payload) =>
  api.post('/admin/marketing/campaigns/apply', payload);

// Membresías (Admin)
export const getMemberships = () => api.get('/admin/marketing/memberships');
export const saveMembership = (payload) => api.post('/admin/marketing/memberships', payload);
export const deleteMembership = (id) => api.delete(`/admin/marketing/memberships?id=${id}`);
export const purchaseMembership = (userId, membershipId) => api.post('/admin/marketing/memberships/purchase', { userId, membershipId });

// Membresías (Usuario)
export const getUserMemberships = (userId) => api.get('/user/memberships', { params: { userId } });
export const applyMembershipDiscount = (userId, serviceId, price) =>
  api.post('/user/memberships/apply-discount', { userId, serviceId, price });
export const incrementMembershipUsage = (userMembershipId) =>
  api.patch('/user/memberships/apply-discount', { userMembershipId });

// FunciÃ³n para actualizar un turno existente (body debe incluir id)
export const updateTurno = (turnoData) => api.put('/turnos', turnoData);

// FunciÃ³n para eliminar un turno (usa query ?id=)
export const deleteTurno = (turnoId) => api.delete(`/turnos?id=${turnoId}`);

// ========== CLIENTES API FUNCTIONS ==========

// Función para obtener todos los clientes (usuarios sin roles admin/colaborador)
export const getClientes = (params = {}) => api.get('/clientes', { params });

// Función para eliminar (soft-delete/remover rol) un cliente por ID
export const deleteCliente = (clienteId) => api.delete(`/clientes/${clienteId}?hard=1`);

// Función para actualizar un cliente por ID
export const updateCliente = (clienteId, data) => api.put(`/clientes/${clienteId}`, data);

// Función para crear un cliente desde panel admin (crea usuario temporal)
export const createCliente = (data) => api.post('/clientes', data);

// Función para eliminar un miembro del equipo
export const deleteMember = (memberId) => api.delete(`/team/${memberId}`);

// FunciÃƒÂ³n para crear un nuevo usuario/cliente
export const createUser = (userData) => {
  const registerData = {
    nombre: userData.nombre,
    apellido: userData.apellido,
    email: userData.email,
    password: userData.password,
    telefono: userData.telefono
  };
  return api.post('/auth/register', registerData);
};

// ========== SERVICIOS API FUNCTIONS ==========

// FunciÃƒÂ³n para obtener todos los servicios
export const getServicios = () => api.get('/servicios');

// FunciÃƒÂ³n para crear un nuevo servicio
export const createServicio = (servicioData) => api.post('/servicios', servicioData);

// FunciÃƒÂ³n para actualizar un servicio existente
export const updateServicio = (servicioData) => api.put('/servicios', servicioData);

// FunciÃƒÂ³n para eliminar un servicio
export const deleteServicio = (servicioId) => api.delete(`/servicios?id=${servicioId}`);

// Intentar obtener SOLO los servicios del colaborador autenticado.
// Prueba endpoints comunes y cae de forma segura a la lista completa de servicios.
export const getMisServicios = async () => {
  // 1) Intentos con endpoints específicos de "mis servicios"
  const tryGet = async (path) => {
    try {
      const { data } = await api.get(path);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    } catch (_) {
      return [];
    }
  };

  // 2) Intento con mapeo servicios-colaboradores (me=1) y normalización
  const tryServiciosColaboradores = async () => {
    try {
      const { data } = await api.get('/servicios-colaboradores?me=1');
      const list = Array.isArray(data) ? data : data?.data;
      if (!Array.isArray(list)) return [];
      const normalized = list.map((x) => {
        const svc = x?.servicio || x?.service || x;
        const id = svc?.id ?? x?.servicio_id ?? x?.service_id;
        const nombre = svc?.nombre ?? x?.servicio_nombre ?? x?.name;
        return { id, nombre };
      }).filter((s) => s && s.id);
      const uniq = [];
      const seen = new Set();
      for (const s of normalized) {
        const key = String(s.id);
        if (!seen.has(key)) { seen.add(key); uniq.push(s); }
      }
      return uniq;
    } catch (_) {
      return [];
    }
  };

  // Secuencia de intentos
  let servicios = await tryGet('/my-services');
  if (!servicios.length) servicios = await tryGet('/mis-servicios');
  if (!servicios.length) servicios = await tryServiciosColaboradores();

  // 3) Fallback final: todos los servicios
  if (!servicios.length) {
    try {
      const { data } = await api.get('/servicios');
      servicios = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    } catch (_) {
      servicios = [];
    }
  }

  // Normalizar estructura mínima {id, nombre}
  return servicios.map((s) => ({ id: s.id ?? s.servicio_id, nombre: s.nombre ?? s.servicio_nombre ?? s.name }));
};

// ========== SERVICIOS-COLABORADORES API FUNCTIONS ==========

// FunciÃƒÂ³n para obtener asignaciones de servicios-colaboradores
export const getServiciosColaboradores = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/servicios-colaboradores${queryString ? `?${queryString}` : ''}`);
};

// FunciÃƒÂ³n para asignar un colaborador a un servicio
export const asignarColaboradorAServicio = (asignacionData) => api.post('/servicios-colaboradores', asignacionData);

// FunciÃƒÂ³n para eliminar asignaciÃƒÂ³n colaborador-servicio
export const eliminarAsignacionColaborador = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return api.delete(`/servicios-colaboradores?${queryString}`);
};

// ========== COLABORADORES POR SERVICIOS API FUNCTIONS ==========

// FunciÃƒÂ³n para obtener colaboradores que pueden realizar ciertos servicios
export const getColaboradoresPorServicios = (servicioIds) => {
  const params = { servicio_ids: Array.isArray(servicioIds) ? servicioIds.join(',') : servicioIds };
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/colaboradores-por-servicios?${queryString}`);
};

// FunciÃƒÂ³n para convertir un cliente en colaborador
export const convertirClienteAColaborador = (clienteId, colaboradorData) => {
  return api.post(`/clientes/${clienteId}/convertir-colaborador`, colaboradorData);
};

// Enviar formulario de contacto
export const sendContact = (data) => api.post('/contact', data);

// ===== ADMIN ROLES =====
export const getAdminRoles = () => api.get('/admin/roles');
export const setAdminRole = (usuarioId, makeAdmin) =>
  api.post('/admin/roles', { usuario_id: usuarioId, make_admin: !!makeAdmin });

// ====== BOXES (CONSULTORIOS) CRUD ======
export const getBoxes = () => api.get('/boxes');
export const createBox = (payload) => api.post('/boxes', payload);
export const updateBox = (id, payload) => api.put(`/boxes/${id}`, payload);
export const deleteBox = (id) => api.delete(`/boxes/${id}`);
export const createBoxRental = (payload) => api.post('/box-rentals', payload);

// ====== BOX-ASIGNACIONES (ASIGNACIÓN SEMANAL) ======
// GET supports filters: fecha, from, to, box_id, box_ids (comma-separated)
export const getBoxAsignaciones = (params = {}) => api.get('/box-asignaciones', { params });
export const createBoxAsignacion = (payload) => api.post('/box-asignaciones', payload);
export const updateBoxAsignacion = (id, payload) => api.put(`/box-asignaciones/${id}`, payload);
export const deleteBoxAsignacion = (id) => api.delete(`/box-asignaciones/${id}`);

// Upload Image
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// ====== SETTINGS ======
export const getSettings = () => api.get('/settings');
export const saveSetting = (key, value) => api.post('/settings', { key, value });
export const saveSettings = (settings) => api.post('/settings', { settings });

// ====== NOTIFICATIONS ======
export const getNotifications = () => api.get('/notifications');
export const createNotification = (payload) => api.post('/notifications', payload);
export const markNotificationRead = (id) => api.post('/notifications/read', { id });



export const cancelMemberRequest = (requestId) => api.put(`/member/requests/${requestId}`, { estado: 'cancelada' });

export default api;
